/**
 * MeshCore Companion Protocol — the real wire format.
 *
 * This is the protocol a MeshCore radio running "Companion" firmware speaks to
 * the app that owns it, over BLE (Nordic UART, one frame per write/notify) or
 * USB serial (framed, see serial-framing.ts). Frame = [code byte][payload].
 *
 * Layouts are taken from meshcore_py 2.3.x (`commands/*.py`, `reader.py`),
 * and are bench-proven in MeshBell (`edge/pi-node/radio.py`) on a Heltec V4
 * and a MeshPocket. All multi-byte integers are little-endian.
 *
 * Pure functions only: no I/O, no timers. client.ts does the talking.
 */

// ============= Command codes (app -> radio) =============

export const CMD = {
  APP_START: 1,
  SEND_TXT_MSG: 2,
  SEND_CHANNEL_TXT_MSG: 3,
  GET_CONTACTS: 4,
  GET_DEVICE_TIME: 5,
  SET_DEVICE_TIME: 6,
  SEND_SELF_ADVERT: 7,
  SET_ADVERT_NAME: 8,
  SYNC_NEXT_MESSAGE: 10,
  SET_RADIO_PARAMS: 11,
  SET_RADIO_TX_POWER: 12,
  REBOOT: 19,
  DEVICE_QUERY: 22,
  GET_CHANNEL: 31,
  SET_CHANNEL: 32,
  GET_STATS: 56,
  SEND_CONTROL_DATA: 55,
} as const

// ============= Response + push codes (radio -> app) =============

export const RESP = {
  OK: 0,
  ERROR: 1,
  CONTACT_START: 2,
  CONTACT: 3,
  CONTACT_END: 4,
  SELF_INFO: 5,
  MSG_SENT: 6,
  CONTACT_MSG_RECV: 7,
  CHANNEL_MSG_RECV: 8,
  CURRENT_TIME: 9,
  NO_MORE_MSGS: 10,
  DEVICE_INFO: 13,
  CONTACT_MSG_RECV_V3: 16,
  CHANNEL_MSG_RECV_V3: 17,
  CHANNEL_INFO: 18,
  STATS: 24,
} as const

export const PUSH = {
  ADVERTISEMENT: 0x80,
  PATH_UPDATE: 0x81,
  ACK: 0x82,
  MESSAGES_WAITING: 0x83,
  RAW_DATA: 0x84,
  LOG_RX_DATA: 0x88,
  NEW_ADVERT: 0x8a,
  CONTROL_DATA: 0x8e,
} as const

/** Control-data sub-type: zero-hop node discovery (reply sub-type 0x90). */
export const CONTROL_NODE_DISCOVER_REQ = 0x80

export const STATS_TYPE = { CORE: 0, RADIO: 1, PACKETS: 2 } as const

/**
 * Firmware ceiling for channel text, INCLUDING the "<node name>: " prefix the
 * firmware adds (BaseChatMesh::sendGroupMessage, MAX_TEXT_LEN = 160). Over it,
 * the channel path silently TRUNCATES and still reports OK, so callers must
 * check `channelTextBudget()` and refuse rather than let the firmware cut.
 */
export const MAX_TEXT_LEN = 160

/** A MeshCore channel secret is exactly 16 bytes. */
export const CHANNEL_SECRET_LEN = 16
export const CHANNEL_NAME_LEN = 32

/** App protocol version we announce in APP_START / DEVICE_QUERY. */
export const APP_PROTOCOL_VERSION = 3

// ============= Little-endian helpers =============

function u32(value: number): number[] {
  return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff]
}

/** Byte at `o`, or 0 past the end (decoders length-check before reading). */
function at(b: Uint8Array, o: number): number {
  return b[o] ?? 0
}

function view(b: Uint8Array): DataView {
  return new DataView(b.buffer, b.byteOffset, b.byteLength)
}

function readU16(b: Uint8Array, o: number): number {
  return view(b).getUint16(o, true)
}

function readU32(b: Uint8Array, o: number): number {
  return view(b).getUint32(o, true)
}

function readI32(b: Uint8Array, o: number): number {
  return view(b).getInt32(o, true)
}

function readI8(b: Uint8Array, o: number): number {
  return view(b).getInt8(o)
}

function readI16(b: Uint8Array, o: number): number {
  return view(b).getInt16(o, true)
}

const utf8 = new TextEncoder()
const utf8d = new TextDecoder('utf-8', { fatal: false })

function cString(b: Uint8Array): string {
  const end = b.indexOf(0)
  return utf8d.decode(end >= 0 ? b.subarray(0, end) : b)
}

// ============= Encoders =============

/** APP_START: [1][app_ver][6 reserved][app name]. Reply: SELF_INFO. */
export function encodeAppStart(appName = 'crowdtalkie'): Uint8Array {
  return Uint8Array.from([
    CMD.APP_START,
    APP_PROTOCOL_VERSION,
    0x20, 0x20, 0x20, 0x20, 0x20, 0x20,
    ...utf8.encode(appName),
  ])
}

/** DEVICE_QUERY: [22][app_ver]. Reply: DEVICE_INFO. */
export function encodeDeviceQuery(): Uint8Array {
  return Uint8Array.from([CMD.DEVICE_QUERY, APP_PROTOCOL_VERSION])
}

export interface RadioParams {
  freqMHz: number
  bwKHz: number
  sf: number
  cr: number
}

/**
 * SET_RADIO_PARAMS: [11][freq kHz u32][bw Hz u32][sf][cr]. Reply: OK/ERROR.
 *
 * Field-proven trap (fw v1.17.1): only takes reliably as the FIRST command on
 * a fresh connection, and an OK is not proof. Verify by reconnecting and
 * reading SELF_INFO.radio.
 */
export function encodeSetRadio(p: RadioParams): Uint8Array {
  return Uint8Array.from([
    CMD.SET_RADIO_PARAMS,
    ...u32(Math.round(p.freqMHz * 1000)),
    ...u32(Math.round(p.bwKHz * 1000)),
    p.sf & 0xff,
    p.cr & 0xff,
  ])
}

/** GET_CHANNEL: [31][idx]. Reply: CHANNEL_INFO or ERROR (no such slot). */
export function encodeGetChannel(index: number): Uint8Array {
  return Uint8Array.from([CMD.GET_CHANNEL, index & 0xff])
}

/** SET_CHANNEL: [32][idx][name: 32 bytes, NUL-padded][secret: 16 bytes]. Reply: OK/ERROR. */
export function encodeSetChannel(index: number, name: string, secret: Uint8Array): Uint8Array {
  if (secret.length !== CHANNEL_SECRET_LEN) {
    throw new Error(`MeshCore channel secret must be ${CHANNEL_SECRET_LEN} bytes, got ${secret.length}`)
  }
  const nameBytes = new Uint8Array(CHANNEL_NAME_LEN)
  nameBytes.set(utf8.encode(name).subarray(0, CHANNEL_NAME_LEN - 1))
  const out = new Uint8Array(2 + CHANNEL_NAME_LEN + CHANNEL_SECRET_LEN)
  out[0] = CMD.SET_CHANNEL
  out[1] = index & 0xff
  out.set(nameBytes, 2)
  out.set(secret, 2 + CHANNEL_NAME_LEN)
  return out
}

/**
 * Bytes of text the app may hand the radio for one channel message, given
 * the node's name: the firmware prepends "<name>: " inside MAX_TEXT_LEN.
 */
export function channelTextBudget(nodeName: string): number {
  return MAX_TEXT_LEN - utf8.encode(nodeName).length - 2
}

/**
 * SEND_CHANNEL_TXT_MSG: [3][txt_type=0][channel idx][timestamp u32][utf-8 text].
 * Reply: OK (queued, NOT radiated) or ERROR.
 */
export function encodeChannelText(channelIndex: number, text: string, timestampSec: number): Uint8Array {
  return Uint8Array.from([
    CMD.SEND_CHANNEL_TXT_MSG,
    0,
    channelIndex & 0xff,
    ...u32(timestampSec >>> 0),
    ...utf8.encode(text),
  ])
}

/** SYNC_NEXT_MESSAGE: [10]. Reply: a *_MSG_RECV(_V3) frame or NO_MORE_MSGS. */
export function encodeSyncNextMessage(): Uint8Array {
  return Uint8Array.from([CMD.SYNC_NEXT_MESSAGE])
}

/** SEND_SELF_ADVERT: [7] zero-hop, [7][1] flood. Reply: OK/ERROR. */
export function encodeSelfAdvert(flood = false): Uint8Array {
  return flood ? Uint8Array.from([CMD.SEND_SELF_ADVERT, 1]) : Uint8Array.from([CMD.SEND_SELF_ADVERT])
}

/**
 * Zero-hop node discovery: [55][0x80 | prefix-only][filter][tag u32].
 * filter 0xFF = every node type. Replies arrive as CONTROL_DATA pushes.
 * Zero-hop, so it costs one short frame and is never repeated by the mesh.
 */
export function encodeNodeDiscover(tag: number, filter = 0xff, prefixOnly = true): Uint8Array {
  return Uint8Array.from([
    CMD.SEND_CONTROL_DATA,
    CONTROL_NODE_DISCOVER_REQ | (prefixOnly ? 1 : 0),
    filter & 0xff,
    ...u32(tag >>> 0),
  ])
}

/** GET_STATS: [56][stats type]. Reply: STATS. */
export function encodeGetStats(type: number): Uint8Array {
  return Uint8Array.from([CMD.GET_STATS, type & 0xff])
}

// ============= Decoded frame types =============

export interface SelfInfo {
  advType: number
  txPower: number
  maxTxPower: number
  publicKey: string
  advLat: number
  advLon: number
  radio: RadioParams
  name: string
}

export interface DeviceInfo {
  firmwareVersion: number
  maxContacts?: number
  maxChannels?: number
  firmwareBuild?: string
  model?: string
  version?: string
}

export interface ChannelInfo {
  index: number
  name: string
  secret: Uint8Array
}

export interface ChannelMessage {
  channelIndex: number
  /** Hops the flood took; undefined for a direct (path_len 255) message. */
  pathLen?: number
  txtType: number
  senderTimestamp: number
  /** Includes the sender's "<name>: " prefix, as the firmware delivers it. */
  text: string
  snr?: number
}

export interface PacketStats {
  recv: number
  sent: number
  floodTx: number
  directTx: number
  floodRx: number
  directRx: number
}

export interface CoreStats {
  batteryMv: number
  uptimeSecs: number
  errors: number
  queueLen: number
}

export interface RadioStats {
  noiseFloor: number
  lastRssi: number
  lastSnr: number
  txAirSecs: number
  rxAirSecs: number
}

export type CompanionFrame =
  | { kind: 'ok'; value?: number }
  | { kind: 'error'; code?: number }
  | { kind: 'self_info'; info: SelfInfo }
  | { kind: 'device_info'; info: DeviceInfo }
  | { kind: 'channel_info'; info: ChannelInfo }
  | { kind: 'channel_msg'; msg: ChannelMessage }
  | { kind: 'no_more_msgs' }
  | { kind: 'stats_core'; stats: CoreStats }
  | { kind: 'stats_radio'; stats: RadioStats }
  | { kind: 'stats_packets'; stats: PacketStats }
  | { kind: 'push_messages_waiting' }
  | { kind: 'push_log_rx'; snr: number; rssi: number; raw: Uint8Array }
  | { kind: 'push_advert' }
  | { kind: 'push_control'; payload: Uint8Array }
  | { kind: 'other'; code: number; payload: Uint8Array }

/** True for frames the radio sends unprompted (never a command reply). */
export function isPush(frame: CompanionFrame): boolean {
  return frame.kind.startsWith('push_') || (frame.kind === 'other' && frame.code >= 0x80)
}

// ============= Decoder =============

export function decodeFrame(frame: Uint8Array): CompanionFrame {
  if (frame.length === 0) return { kind: 'other', code: -1, payload: frame }
  const code = at(frame, 0)
  const p = frame.subarray(1)

  switch (code) {
    case RESP.OK:
      return frame.length === 5 ? { kind: 'ok', value: readU32(frame, 1) } : { kind: 'ok' }

    case RESP.ERROR:
      return frame.length > 1 ? { kind: 'error', code: at(frame, 1) } : { kind: 'error' }

    case RESP.SELF_INFO: {
      // adv_type, tx_power, max_tx_power, pubkey[32], lat i32, lon i32,
      // multi_acks, adv_loc_policy, telemetry_mode, manual_add_contacts,
      // freq u32 (kHz), bw u32 (Hz), sf, cr, name...
      if (p.length < 57) return { kind: 'other', code, payload: p }
      const hex = Array.from(p.subarray(3, 35), (b) => b.toString(16).padStart(2, '0')).join('')
      return {
        kind: 'self_info',
        info: {
          advType: at(p, 0),
          txPower: at(p, 1),
          maxTxPower: at(p, 2),
          publicKey: hex,
          advLat: readI32(p, 35) / 1e6,
          advLon: readI32(p, 39) / 1e6,
          radio: {
            freqMHz: readU32(p, 47) / 1000,
            bwKHz: readU32(p, 51) / 1000,
            sf: at(p, 55),
            cr: at(p, 56),
          },
          name: utf8d.decode(p.subarray(57)).replace(/\0+$/, ''),
        },
      }
    }

    case RESP.DEVICE_INFO: {
      const info: DeviceInfo = { firmwareVersion: at(p, 0) }
      if (info.firmwareVersion >= 3 && p.length >= 79) {
        info.maxContacts = at(p, 1) * 2
        info.maxChannels = at(p, 2)
        info.firmwareBuild = cString(p.subarray(7, 19))
        info.model = cString(p.subarray(19, 59))
        info.version = cString(p.subarray(59, 79))
      }
      return { kind: 'device_info', info }
    }

    case RESP.CHANNEL_INFO: {
      if (p.length < 1 + CHANNEL_NAME_LEN + CHANNEL_SECRET_LEN) return { kind: 'other', code, payload: p }
      return {
        kind: 'channel_info',
        info: {
          index: at(p, 0),
          name: cString(p.subarray(1, 1 + CHANNEL_NAME_LEN)),
          secret: p.slice(1 + CHANNEL_NAME_LEN, 1 + CHANNEL_NAME_LEN + CHANNEL_SECRET_LEN),
        },
      }
    }

    case RESP.CHANNEL_MSG_RECV:
    case RESP.CHANNEL_MSG_RECV_V3: {
      // V3 prepends snr(i8, x4) + 2 reserved bytes.
      const v3 = code === RESP.CHANNEL_MSG_RECV_V3
      const o = v3 ? 3 : 0
      if (p.length < o + 7) return { kind: 'other', code, payload: p }
      const plen = at(p, o + 1)
      let text = utf8d.decode(p.subarray(o + 7))
      if (!v3) text = text.replace(/\0+$/, '')
      return {
        kind: 'channel_msg',
        msg: {
          channelIndex: at(p, o),
          pathLen: plen === 255 ? undefined : plen & 0x3f,
          txtType: at(p, o + 2),
          senderTimestamp: readU32(p, o + 3),
          text,
          snr: v3 ? readI8(p, 0) / 4 : undefined,
        },
      }
    }

    case RESP.NO_MORE_MSGS:
      return { kind: 'no_more_msgs' }

    case RESP.STATS: {
      const type = at(p, 0)
      if (type === STATS_TYPE.CORE && frame.length >= 11) {
        return {
          kind: 'stats_core',
          stats: {
            batteryMv: readU16(frame, 2),
            uptimeSecs: readU32(frame, 4),
            errors: readU16(frame, 8),
            queueLen: at(frame, 10),
          },
        }
      }
      if (type === STATS_TYPE.RADIO && frame.length >= 14) {
        return {
          kind: 'stats_radio',
          stats: {
            noiseFloor: readI16(frame, 2),
            lastRssi: readI8(frame, 4),
            lastSnr: readI8(frame, 5) / 4,
            txAirSecs: readU32(frame, 6),
            rxAirSecs: readU32(frame, 10),
          },
        }
      }
      if (type === STATS_TYPE.PACKETS && frame.length >= 26) {
        return {
          kind: 'stats_packets',
          stats: {
            recv: readU32(frame, 2),
            sent: readU32(frame, 6),
            floodTx: readU32(frame, 10),
            directTx: readU32(frame, 14),
            floodRx: readU32(frame, 18),
            directRx: readU32(frame, 22),
          },
        }
      }
      return { kind: 'other', code, payload: p }
    }

    case PUSH.MESSAGES_WAITING:
      return { kind: 'push_messages_waiting' }

    case PUSH.LOG_RX_DATA:
      // [snr i8 x4][rssi i8][raw over-air packet...]
      return {
        kind: 'push_log_rx',
        snr: p.length > 0 ? readI8(p, 0) / 4 : 0,
        rssi: p.length > 1 ? readI8(p, 1) : 0,
        raw: p.slice(2),
      }

    case PUSH.ADVERTISEMENT:
    case PUSH.NEW_ADVERT:
      return { kind: 'push_advert' }

    case PUSH.CONTROL_DATA:
      return { kind: 'push_control', payload: p.slice() }

    default:
      return { kind: 'other', code, payload: p }
  }
}
